import { ChangeDetectionStrategy, Component, Signal, computed, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdPlanetInfoService } from '../bodygraph/hd-planet-info.service';
import { getHdPlanetDisplayConfig } from '../bodygraph/planet-grid/hd-planet-display-config';
import {
  HD_ZODIAC_ASTRONOMICON_GLYPHS,
  hdZodiacPositionFromLongitude,
} from '../bodygraph/hd-gate-coordinates';
import { formatHdZodiacDegrees } from '../bodygraph/gate-info/variants/hd-zodiac-position.util';
import {
  HdFixedStarDefinition,
  HdFixedStarPosition,
  getHdFixedStarBySide,
} from './hd-fixed-stars.metadata';
import { formatHdFixedStarDistance, hdFixedStarLongitude2000 } from './hd-fixed-stars.util';
// De service opent dit component (module-cycle), maar beide referenties worden
// pas bij runtime-gebruik geëvalueerd; zie ook het type-only gebruik hieronder.
import { HdFixedStarPanelData, HdFixedStarsPanelService } from './hd-fixed-stars-panel.service';
import { HdSixtySixSidesDiagramComponent } from './hd-sixty-six-sides-diagram.component';
import { HdFixedStarSkyChartComponent } from './hd-fixed-star-sky-chart.component';

interface PositionVm {
  readonly degreesText: string;
  readonly glyph: string;
  readonly signLabel: string;
  readonly gateLine: string;
  readonly variantLabel: string;
}

interface ContextRowVm {
  readonly label: string;
  readonly value: string;
  /** Dierenriem-glyph (Astronomicon) achter de waarde. */
  readonly glyph?: string;
  /** Planeet-glyph (Astronomicon) vóór de waarde. */
  readonly planetGlyph?: string;
}

interface FixedStarPanelVm {
  readonly star: HdFixedStarDefinition;
  readonly sideLabel: string;
  readonly altNames: string;
  readonly keynote: string;
  readonly character: string;
  readonly effectLabel: string;
  readonly description: string;
  readonly positions: readonly PositionVm[];
  readonly sharesNote: string;
  readonly contextTitle: string;
  readonly contextRows: readonly ContextRowVm[];
  readonly shift2027Title: string;
  readonly shift2027: string;
  readonly skyChartLabel: string;
  readonly moreInfoLabel: string;
  readonly labels: {
    readonly keynote: string;
    readonly character: string;
    readonly effect: string;
    readonly position2000: string;
    readonly gateLine: string;
    readonly alsoKnownAs: string;
  };
}

/**
 * Info-paneel voor één fixed star van de 66 Sides: keynote/karakter/effect
 * uit de `human-design/fixed-stars`-scope, de 2000-positie(s) met
 * dierenriem-glyph en poort.lijn, de chart-context van de klik (activatie,
 * precessie-correctie, afstand) en de 66 Sides-tekening waarin deze side
 * gemarkeerd is. Onder de tekening opent een knop de algemene info-pagina
 * (kosmologie + berekening). Geopend via {@link HdFixedStarsPanelService}.
 */
@Component({
  selector: 'hd-fixed-star-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdSixtySixSidesDiagramComponent, HdFixedStarSkyChartComponent],
  template: `
    <section class="fixed-star-panel">
      <header class="fixed-star-panel__header">
        <h3 class="fixed-star-panel__title">{{ vm().star.name }}</h3>
        <span class="fixed-star-panel__side-badge">{{ vm().sideLabel }}</span>
      </header>
      @if (vm().altNames) {
        <p class="fixed-star-panel__alt">{{ vm().labels.alsoKnownAs }}: {{ vm().altNames }}</p>
      }

      @if (vm().keynote) {
        <p class="fixed-star-panel__keynote">{{ vm().keynote }}</p>
      }

      <section class="fixed-star-panel__sky">
        <h4>{{ vm().skyChartLabel }}</h4>
        <hd-fixed-star-sky-chart [side]="vm().star.side" (enlarge)="openSkyChart()" />
      </section>

      <dl class="fixed-star-panel__facts">
        <!-- Karakter (planetaire natuur) en Effect (fortuinklasse) zijn bewust
             verborgen: puur astrologische referenties die te sturend zijn. De data
             blijft in de metadata/i18n staan zodat ze eenvoudig terug aan kan. -->
        @for (pos of vm().positions; track $index) {
          <div class="fixed-star-panel__fact">
            <dt>{{ vm().labels.position2000 }}@if (pos.variantLabel) { <span class="fixed-star-panel__variant">({{ pos.variantLabel }})</span>}</dt>
            <dd>
              {{ pos.degreesText }}
              <span class="fixed-star-panel__zodiac-glyph" aria-hidden="true">{{ pos.glyph }}</span>
              {{ pos.signLabel }}
              <span class="fixed-star-panel__gate-line">· {{ vm().labels.gateLine }} {{ pos.gateLine }}</span>
            </dd>
          </div>
        }
      </dl>

      @if (vm().sharesNote) {
        <p class="fixed-star-panel__note">{{ vm().sharesNote }}</p>
      }

      @if (vm().description) {
        <p class="fixed-star-panel__description">{{ vm().description }}</p>
      }

      @if (vm().shift2027) {
        <section class="fixed-star-panel__shift2027">
          <h4>{{ vm().shift2027Title }}</h4>
          <p class="fixed-star-panel__description">{{ vm().shift2027 }}</p>
        </section>
      }

      @if (vm().contextRows.length > 0) {
        <section class="fixed-star-panel__context">
          <h4>{{ vm().contextTitle }}</h4>
          <dl class="fixed-star-panel__facts">
            @for (row of vm().contextRows; track row.label) {
              <div class="fixed-star-panel__fact">
                <dt>{{ row.label }}</dt>
                <dd>
                  @if (row.planetGlyph) {
                    <span class="fixed-star-panel__planet-glyph" aria-hidden="true">{{ row.planetGlyph }}</span>
                  }
                  {{ row.value }}
                  @if (row.glyph) {
                    <span class="fixed-star-panel__zodiac-glyph" aria-hidden="true">{{ row.glyph }}</span>
                  }
                </dd>
              </div>
            }
          </dl>
        </section>
      }

      <section class="fixed-star-panel__diagram">
        <hd-sixty-six-sides-diagram [highlightSide]="vm().star.side" [ariaLabel]="'66 Sides'" />
      </section>

      <button type="button" class="fixed-star-panel__more-info" (click)="openGeneralInfo($event)">
        <i class="fas fa-circle-info" aria-hidden="true"></i>
        {{ vm().moreInfoLabel }}
      </button>
    </section>
  `,
  styles: `
    .fixed-star-panel {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 14px 16px 18px;
      max-width: min(560px, 90vw);
      color: #3d414e;
    }

    .fixed-star-panel__header {
      display: flex;
      align-items: baseline;
      gap: 10px;
    }

    .fixed-star-panel__title {
      margin: 0;
      font-family: 'Bw Modelica', 'Raleway', sans-serif;
      font-weight: 900;
      font-size: 20px;
    }

    .fixed-star-panel__side-badge {
      padding: 2px 8px;
      border-radius: 10px;
      background: #f1ece2;
      border: 1px solid #d9cdb4;
      font-size: 12px;
      font-weight: 700;
    }

    .fixed-star-panel__alt {
      margin: -6px 0 0;
      font-size: 12.5px;
      opacity: 0.7;
    }

    .fixed-star-panel__keynote {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
    }

    .fixed-star-panel__facts {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin: 0;
    }

    .fixed-star-panel__fact {
      display: flex;
      gap: 8px;
      font-size: 13px;

      dt {
        flex: 0 0 auto;
        min-width: 130px;
        font-weight: 600;
        opacity: 0.65;
      }

      dd {
        margin: 0;
      }
    }

    .fixed-star-panel__variant {
      font-weight: 400;
    }

    .fixed-star-panel__zodiac-glyph {
      font-family: 'Astronomicon', serif;
      font-size: 1.45em;
      font-weight: 700;
      line-height: 1;
    }

    .fixed-star-panel__planet-glyph {
      font-family: 'Astronomicon', serif;
      font-size: 1.5em;
      line-height: 1;
      margin-right: 2px;
    }

    .fixed-star-panel__gate-line {
      opacity: 0.65;
    }

    .fixed-star-panel__note,
    .fixed-star-panel__description {
      margin: 0;
      font-size: 12.5px;
      line-height: 1.45;
      opacity: 0.85;
    }

    .fixed-star-panel__description {
      white-space: pre-line;
    }

    .fixed-star-panel__context h4,
    .fixed-star-panel__shift2027 h4,
    .fixed-star-panel__sky h4 {
      margin: 4px 0 6px;
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      opacity: 0.7;
    }

    .fixed-star-panel__diagram {
      margin-top: 4px;
      padding: 8px;
      background: #fafafa;
      border: 1px solid #e6e6e6;
      border-radius: 8px;
    }

    .fixed-star-panel__more-info {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      align-self: flex-start;
      padding: 6px 12px;
      border: 1px solid #d9cdb4;
      border-radius: 8px;
      background: #f7f3ea;
      color: inherit;
      font: inherit;
      font-size: 12.5px;
      font-weight: 600;
      cursor: pointer;

      &:hover {
        background: #f1ece2;
      }
    }
  `,
})
export class HdFixedStarPanelComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);
  private readonly panelService = inject(HdFixedStarsPanelService);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdFixedStarPanelData>
    | null;

  constructor() {
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected readonly vm = computed<FixedStarPanelVm>(() => {
    const lang = this.contentLang.effectiveLang();
    const data = this.panelData?.() ?? { side: 1, context: null };
    const star = getHdFixedStarBySide(data.side) ?? getHdFixedStarBySide(1)!;
    const shared = star.sharesPositionWithSide != null
      ? getHdFixedStarBySide(star.sharesPositionWithSide)
      : undefined;

    const sharesNote = shared
      ? this.text('panel.sharesPositionWith')
          .replace('{{side}}', String(shared.side))
          .replace('{{name}}', shared.name)
      : '';

    return {
      star,
      sideLabel: `${this.text('panel.sideNumber')} ${star.side}`,
      altNames: star.altNames.join(', '),
      keynote: this.starText(star.key, 'keynote'),
      character: this.starText(star.key, 'character'),
      effectLabel: star.effect ? this.text(`panel.effect.${star.effect}`) : '',
      description: this.starText(star.key, 'description'),
      positions: star.positions.map(position => this.positionVm(position, lang)),
      sharesNote,
      contextTitle: this.text('panel.context.title'),
      contextRows: this.contextRows(data, lang),
      shift2027Title: this.text('panel.shift2027Title'),
      shift2027: this.starText(star.key, 'shift2027'),
      skyChartLabel: this.text('panel.skyChartLabel'),
      moreInfoLabel: this.text('generalInfo.buttonLabel'),
      labels: {
        keynote: this.text('panel.keynoteLabel'),
        character: this.text('panel.characterLabel'),
        effect: this.text('panel.effectLabel'),
        position2000: this.text('panel.position2000Label'),
        gateLine: this.text('panel.gateLineLabel'),
        alsoKnownAs: this.text('panel.alsoKnownAs'),
      },
    };
  });

  private positionVm(position: HdFixedStarPosition, lang: string): PositionVm {
    const zodiac = hdZodiacPositionFromLongitude(hdFixedStarLongitude2000(position));
    return {
      degreesText: formatHdZodiacDegrees(zodiac),
      glyph: HD_ZODIAC_ASTRONOMICON_GLYPHS[position.sign],
      signLabel: this.gateInfo.zodiacLabelIn(position.sign, lang),
      gateLine: position.gateLine,
      variantLabel: position.variantKey ? this.text(`panel.variantLabel.${position.variantKey}`) : '',
    };
  }

  private contextRows(data: HdFixedStarPanelData, lang: string): ContextRowVm[] {
    const context = data.context;
    if (!context) {
      return [];
    }
    const planetName = this.planetInfo.planetTranslationsAllIn(lang)[context.planetKey]?.name
      ?? context.planetKey;
    const sideLabel = this.transloco.translate<string>(
      `humanDesign.bodygraphSide.${context.activationSide}`, undefined, lang,
    );
    const chartPos = hdZodiacPositionFromLongitude(context.activationLongitude);
    const correctedPos = hdZodiacPositionFromLongitude(context.correctedLongitude);
    const correction = context.correctionDegrees;
    const correctionText = `${correction >= 0 ? '+' : '−'}${formatHdFixedStarDistance(Math.abs(correction))}`;
    return [
      {
        label: this.text('panel.context.activation'),
        value: `${sideLabel} ${planetName} ${context.gate}.${context.line}`,
        planetGlyph: getHdPlanetDisplayConfig(context.planetKey).astronomiconChar,
      },
      {
        label: this.text('panel.context.chartLongitude'),
        value: `${formatHdZodiacDegrees(chartPos)} ${this.gateInfo.zodiacLabelIn(chartPos.zodiacSign, lang)}`,
        glyph: HD_ZODIAC_ASTRONOMICON_GLYPHS[chartPos.zodiacSign],
      },
      { label: this.text('panel.context.correction'), value: correctionText },
      {
        label: this.text('panel.context.corrected'),
        value: `${formatHdZodiacDegrees(correctedPos)} ${this.gateInfo.zodiacLabelIn(correctedPos.zodiacSign, lang)}`,
        glyph: HD_ZODIAC_ASTRONOMICON_GLYPHS[correctedPos.zodiacSign],
      },
      { label: this.text('panel.context.distance'), value: formatHdFixedStarDistance(context.distanceDegrees) },
    ];
  }

  protected openGeneralInfo(event: MouseEvent): void {
    this.panelService.openGeneralInfo({ x: event.clientX, y: event.clientY });
  }

  protected openSkyChart(): void {
    this.panelService.openSkyChart(this.vm().star.side);
  }

  private starText(starKey: string, field: string): string {
    return this.text(`stars.${starKey}.${field}`);
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('fixedStars', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('fixedStars', key, 'nl');
  }
}
